import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import {
  Box,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getPromptAnswerEntries } from '../../../components/Inspections/getPromptAnswer'
import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  EntryWithRelated,
  NotInspectedDummyEntry
} from '../../../models/inspections'
import { useUpdateInspectionEntryMutation } from '../../../redux/api/inspectionApi'
import { useGetInspectionEntryStatusQuery } from '../../../redux/api/shopMatrixApi'
import { PromptInfoTypeEnum } from '../../../sdk'
import {
  checkSinglePromptIsComplete,
  PromptError
} from '../../../utils/checkInspectionEntryRequiredNotesOrPhoto'
import { ActionSelect } from './ActionSelect'
import EntryMediaButton from './EntryMediaButton'
import OpenNotesDialog from './OpenNotesDialog'
import PromptNotesDialog from './PromptNotesDialog'
import { PromptSelectForMultiSelect } from './PromptSelectForMultiSelect'
import { PromptSelectForSingleSelect } from './PromptSelectForSingleSelect'
import { SelectInspectionEntryStatusChip } from './SelectInspectionEntryStatusChip'

export const SinglePromptOfEntry = ({
  entry: entryRaw
}: {
  entry: EntryWithRelated
}) => {
  // this state is as cached data, will not sync with backend until user refresh the page
  const [entry, setEntry] = useState(entryRaw)

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [updateEntry] = useUpdateInspectionEntryMutation()
  const { data: statuses } = useGetInspectionEntryStatusQuery({
    authorization: accessToken,
    shop: shopId,
    isActive: true
  })

  const allStatuses = useMemo(
    () =>
      !entry.info!.customStatus ||
      statuses?.find((s) => s.id === entry.info!.customStatus)
        ? statuses && [...statuses, NotInspectedDummyEntry]
        : statuses &&
          entry.status && [
            ...statuses,
            {
              ...entry.status,
              info: {
                ...entry.status.info!,
                title: entry.status.info!.title + ' (Outdated)'
              }
            },
            NotInspectedDummyEntry
          ],
    [entry.info, entry.status, statuses]
  )

  const prompt = entry.prompts[0]

  const [error, setError] = useState<PromptError>({
    status: false,
    answer: false,
    notes: false,
    photo: false
  })
  const answers = useMemo(
    () => (prompt ? getPromptAnswerEntries(prompt) : []),
    [prompt]
  )
  useEffect(() => {
    if (!prompt) {
      setError({ status: false, answer: false, notes: false, photo: false })
    } else {
      setError(checkSinglePromptIsComplete(prompt, entry))
    }
  }, [entry, prompt])

  const handlePass = useCallback(() => {
    const updatedEntry = {
      ...entry,
      info: {
        ...entry.info,
        customStatus: allStatuses?.find((s) => !s.info!.shop)?.id
      },
      status: allStatuses?.find((s) => !s.info!.shop)!
    }
    // update UI before sending request
    setEntry(updatedEntry)
    updateEntry({
      authorization: accessToken,
      entryEntry: updatedEntry
    })
  }, [accessToken, entry, allStatuses, updateEntry])

  const errorMessage = useMemo(() => {
    let message = ''
    if (error.status) {
      message += 'Please select a status. '
    }
    if (error.answer) {
      message += 'Answer is required. '
    }
    if (error.notes) {
      message += 'Notes are required. '
    }
    if (error.photo) {
      message += 'Photo is required. '
    }
    return message
  }, [error.answer, error.notes, error.photo, error.status])
  const [openPromptNotesDialog, setOpenPromptNotesDialog] = useState(false)
  const [openMediaDialog, setOpenMediaDialog] = useState(false)
  const [openActionSelect, setOpenActionSelect] = useState(false)
  const handleSelectAnswer = useCallback(
    (updatedEntry: EntryWithRelated) => {
      const updatedAnswers = getPromptAnswerEntries(updatedEntry.prompts[0])
      if (updatedAnswers.length === 0) {
        return
      }
      const error = checkSinglePromptIsComplete(entry.prompts[0], entry)
      if (error.notes) {
        setOpenPromptNotesDialog(true)
      }
      if (error.photo) {
        setOpenMediaDialog(true)
      }
      // if answer requires canned jobs
      if (updatedAnswers.flatMap((a) => a.answerToCannedJobs).length > 0) {
        setOpenActionSelect(true)
      }
    },
    [entry]
  )

  if (!prompt) {
    return <></>
  }

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack
        direction="row"
        justifyContent={'space-between'}
        spacing={1}
        alignItems={'center'}>
        <>
          {!entry.info!.customStatus ? (
            <IconButton onClick={handlePass}>
              <RadioButtonUncheckedIcon />
            </IconButton>
          ) : !error.answer && !error.notes && !error.photo ? (
            <Box p={1}>
              <CheckCircleIcon color="success" />
            </Box>
          ) : (
            <Box p={1}>
              <Tooltip title={errorMessage}>
                <ErrorIcon color="error" />
              </Tooltip>
            </Box>
          )}
        </>
        <Stack
          spacing={1}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            maxWidth: '100%'
          }}>
          <Stack
            direction="row"
            justifyContent={'space-between'}
            spacing={1}
            alignItems={'center'}>
            <Typography variant="subtitle1">
              {`${entry.info!.title}: ${prompt.info!.question}${
                prompt.info!.required ? ' *' : ''
              }`}
            </Typography>
            <SelectInspectionEntryStatusChip
              entry={entry}
              setEntry={setEntry}
              allStatuses={allStatuses}
            />
          </Stack>
          <Stack direction="row" justifyContent={'space-between'} spacing={2}>
            <Stack
              spacing={1}
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                maxWidth: '100%'
              }}>
              {prompt.info!.type === PromptInfoTypeEnum.MultipleChoice ? (
                <PromptSelectForSingleSelect
                  prompt={prompt}
                  entry={entry}
                  setEntry={setEntry}
                  allStatuses={allStatuses}
                  handleSelectAnswer={handleSelectAnswer}
                />
              ) : prompt.info!.type ===
                PromptInfoTypeEnum.MultipleChoiceMultipleSelect ? (
                <PromptSelectForMultiSelect
                  prompt={prompt}
                  entry={entry}
                  setEntry={setEntry}
                  allStatuses={allStatuses}
                  handleSelectAnswer={handleSelectAnswer}
                />
              ) : (
                <></>
              )}
              <ActionSelect
                answers={answers}
                entry={entry}
                setEntry={setEntry}
                open={openActionSelect}
                setOpen={setOpenActionSelect}
              />
              {error.answer && (
                <FormHelperText error>
                  * This question is required
                </FormHelperText>
              )}
            </Stack>
            <Stack alignItems={'flex-end'}>
              <Stack direction={'row'} spacing={1} pr={1}>
                {prompt.info!.type === PromptInfoTypeEnum.OpenEnded ? (
                  <OpenNotesDialog
                    prompt={prompt}
                    entry={entry}
                    setEntry={setEntry}
                  />
                ) : (
                  <PromptNotesDialog
                    prompt={prompt}
                    entry={entry}
                    setEntry={setEntry}
                    open={openPromptNotesDialog}
                    setOpen={setOpenPromptNotesDialog}
                  />
                )}
                <EntryMediaButton
                  media={[...prompt.media]}
                  promptId={prompt.id!}
                  setEntry={setEntry}
                  open={openMediaDialog}
                  setOpen={setOpenMediaDialog}
                />
              </Stack>
              {((prompt.info!.requiresNotes && !prompt.info!.technicianNotes) ||
                error.notes) && (
                <FormHelperText error>* Notes are required</FormHelperText>
              )}
              {((prompt.media.length === 0 && prompt.info!.requiresPhoto) ||
                error.photo) && (
                <FormHelperText error>* Photos are required</FormHelperText>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}
